import React from 'react';
import s from './solution-page-header.scss';
import type { IDetailsProps } from '../solution-info';
import { AddToSite } from '../solution-info';
import { config } from '../../../../config';
import { Origin } from '../../../../enums/marketplace-enums';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { Box, Avatar, Text, Badge } from '@wix/design-system';
import { getClientImageURL } from '../../../../common/getClientImageURL';
import { composer } from '../../../../component-decorators/composer';
import type { TFunction } from 'i18next';
import { computed } from 'mobx';
import {
  getBundleAppSavedPrice,
  isBundleAppAndInstalled,
  isBundleAppAndNotInstalled,
} from '../../../../common/utils';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { withExperiments } from '../../../../component-decorators/with-experiments';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { withTranslation } from '../../../../component-decorators/with-translation';
import { BackButton } from '../../../back-button';
import {
  getAppBadgeTitle,
  getAppBadgeTheme,
  SolutionBadge,
} from '@wix/app-market-collection-widget';
import { IncompleteSetupReason } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import type { IAppDependenciesProps } from '../solution-page';

interface ISolutionHeaderProps
  extends Partial<InjectedExperimentsProps>,
    Partial<IDetailsProps> {
  marketplaceStore?: MarketplaceStore;
  show: boolean;
  position: 'top' | 'bottom';
  isMobile?: boolean;
  appId: string;
  appDependencies: IAppDependenciesProps;
}

@withMarketplaceStore
@withExperiments
@withTranslation
export class SolutionPageHeader extends React.Component<ISolutionHeaderProps> {
  @computed get isAppInstalled() {
    return this.props.marketplaceStore.installedApps?.some(
      (installedApp) =>
        installedApp.appId === this.props.id &&
        installedApp.installationDetails.incompleteSetupReason !==
          IncompleteSetupReason.PENDING_REQUIRED_INSTALLATIONS,
    );
  }

  render() {
    const solutionPageHeaderContainer =
      config.origin === Origin.STANDALONE
        ? s.solutionPageHeaderContainerSa
        : s.solutionPageHeaderContainer;

    const bottomTransform =
      this.props.position === 'bottom'
        ? this.props.show
          ? 'translateY(0%)'
          : 'translateY(100%)'
        : undefined;
    const bottomTransition =
      this.props.position === 'bottom' ? 'all 0.8s ease-out' : undefined;
    const bottomPosition = this.props.position === 'bottom' ? 0 : undefined;
    const topOpacity =
      this.props.position === 'top' ? (this.props.show ? 1 : 0) : undefined;
    const isBundleInstalled = isBundleAppAndInstalled(
      this.props.appId,
      this.props.marketplaceStore.bundleApps,
    );
    const appBadgeKey = getAppBadgeTitle(
      isBundleInstalled,
      false,
      this.props?.Tags,
      this.props?.experiments,
      true,
      this.props.appId,
    );
    const badgeTheme = appBadgeKey ? getAppBadgeTheme(appBadgeKey) : {};
    const shouldShowBackButton =
      this.props.marketplaceStore.prevRoute &&
      this.props.marketplaceStore.prevRoute.currentUrl !==
        this.props.marketplaceStore.route.currentUrl &&
      this.props.marketplaceStore.route.referral !== 'back_button' &&
      this.props.marketplaceStore.route.referral !== 'menu_navigation';

    return (
      <Box
        opacity={topOpacity}
        transform={bottomTransform}
        transition={bottomTransition}
        bottom={bottomPosition}
        verticalAlign="middle"
        className={solutionPageHeaderContainer}
      >
        {shouldShowBackButton && (
          <Box className={s.headerBack}>
            <BackButton
              skinColor="standard"
              hideText={true}
              appId={this.props.id}
            />
          </Box>
        )}
        <Box
          marginLeft={
            this.props.isMobile && shouldShowBackButton
              ? '0px'
              : this.props.isMobile || shouldShowBackButton
              ? '6px'
              : '48px'
          }
          marginRight="12px"
        >
          <Avatar
            shape="square"
            dataHook="solution-icon-avatar"
            className={s.headerAvatar}
            size="size36"
            imgProps={{
              src: getClientImageURL(this.props.icon, {
                toHeight: 36,
                toWidth: 36,
              }),
              alt: `${this.props.name} logo`,
            }}
          />
        </Box>

        <Box direction="vertical" className={s.appTitleAndCompanyContainer}>
          <Box>
            <Text size={this.props.isMobile ? 'small' : 'medium'} weight="bold">
              {this.props.name}
            </Text>
            {appBadgeKey && (
              <Box marginLeft="12px" marginTop="5px">
                <Badge dataHook="solution-badge" {...badgeTheme}>
                  {this.props.t(appBadgeKey)}
                </Badge>
              </Box>
            )}
          </Box>
          <Box>
            <Text size="small">
              <TranslatedKey tkey="solution.page.developerName.by" />
            </Text>
            <Text size="small" className={s.headerDevLink}>
              {' '}
              {this.props.developerName}
            </Text>
          </Box>
        </Box>
        {!this.props.isMobile && (
          <div
            className={s.headerBtnAndPricing}
            style={{
              flexDirection: this.props.isMobile ? 'column-reverse' : 'initial',
              alignItems: this.props.isMobile ? 'end' : 'center',
            }}
          >
            <SolutionBadgeWrapper
              isInstalled={this.isAppInstalled}
              showPremiumLabel={
                this.props.pricing &&
                this.props.pricing.isRequiredWixPremium &&
                !this.props.marketplaceStore.siteIsPremium
              }
              pricing={this.props.pricing}
              isBundleAppNotInstalled={isBundleAppAndNotInstalled(
                this.props.id,
                this.props.marketplaceStore.bundleApps,
              )}
              isBundleAppInstalled={isBundleAppAndInstalled(
                this.props.id,
                this.props.marketplaceStore.bundleApps,
              )}
              savedPrice={getBundleAppSavedPrice(
                this.props.id,
                this.props.marketplaceStore.bundleApps,
              )}
              companyId={this.props.companyId}
              isPreInstalled={this.props.showRegularAppPage}
            />
            <div
              className={s.headerAddToSiteBtn}
              data-hook="btn-install-cta-header"
            >
              <AddToSite
                shouldShowTooltip={topOpacity}
                addToSiteData={this.props}
                size="small"
                location="sticky_header"
                appDependencies={this.props.appDependencies}
              />
            </div>
          </div>
        )}
      </Box>
    );
  }
}

const TranslatedKey = composer()
  .withTranslation()
  .compose(({ t, tkey }: { t?: TFunction; tkey: string }) => {
    return t(tkey);
  });

const SolutionBadgeWrapper = composer()
  .withTranslation()
  .compose(
    ({
      t,
      isInstalled,
      showPremiumLabel,
      pricing,
      isBundleAppInstalled,
      isBundleAppNotInstalled,
      savedPrice,
      companyId,
      isPreInstalled,
    }: {
      t?: TFunction;
      isInstalled: boolean;
      showPremiumLabel: boolean;
      pricing: any;
      isBundleAppInstalled?: boolean;
      isBundleAppNotInstalled?: boolean;
      savedPrice?: number;
      companyId?: string;
      isPreInstalled?: boolean;
    }) => (
      <SolutionBadge
        isInstalled={isInstalled}
        t={(k, o) => t(k, o)}
        showPremiumLabel={showPremiumLabel}
        pricing={pricing}
        isBundleAppNotInstalled={isBundleAppNotInstalled}
        isBundleAppInstalled={isBundleAppInstalled}
        savedPrice={savedPrice}
        companyId={companyId}
        isPreInstalled={isPreInstalled}
      />
    ),
  );
